import * as XLSX from 'xlsx'
import { getTotalIncome } from './totals'
import { readDailyAverageValue } from '@/core/utils/dailyAverageFields'

function safeSheetName(name) {
  return String(name || 'Sheet')
    .replace(/[:\\/?*[\]]/g, '_')
    .slice(0, 31)
}

function escapeCsvCell(val) {
  const s = val == null ? '' : String(val)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function facilitiesLabel(facilities) {
  if (facilities == null) return ''
  try {
    if (Array.isArray(facilities)) {
      return facilities
        .map(f => (typeof f === 'object' && f !== null ? f.name || f.label || f.id : f))
        .join('; ')
    }
    return JSON.stringify(facilities)
  } catch (e) {
    return String(facilities)
  }
}

function buildMetadata(meta) {
  return [
    { Field: 'Start_date', Value: meta?.start ?? '' },
    { Field: 'End_date', Value: meta?.end ?? '' },
    { Field: 'Facilities', Value: facilitiesLabel(meta?.facilities) },
  ]
}

function buildFinancialIndicators(dd) {
  if (!dd?.average || !dd?.totals || !dd?.cash_cc) return []
  const refundVal =
    typeof dd.total_refunds === 'number' || typeof dd.total_refunds === 'string'
      ? parseInt(dd.total_refunds, 10)
      : parseInt(dd.total_refunds?.total ?? dd.total_refunds ?? 0, 10) || 0
  return [
    {
      Indicator: 'Daily Average Income',
      Value: parseInt(dd.average.income_days, 10) || 0,
      Description: 'Daily average income for the selected period',
    },
    {
      Indicator: 'Daily Average Expenses',
      Value: parseInt(dd.average.expenses_days, 10) || 0,
      Description: 'Daily average expenses for the selected period',
    },
    {
      Indicator: 'Net Average Income',
      Value: parseInt(dd.average.net_income_days, 10) || 0,
      Description: 'Daily net average (income - expenses - refunds)',
    },
    {
      Indicator: 'Total Income',
      Value: parseInt(getTotalIncome(dd.totals), 10) || 0,
      Description: 'Total income for the period',
    },
    {
      Indicator: 'Total Expenses',
      Value: parseInt(dd.totals.expenses, 10) || 0,
      Description: 'Total expenses for the period',
    },
    {
      Indicator: 'Total net income',
      Value: parseInt(dd.totals.net_income, 10) || 0,
      Description: 'Total net income for the period',
    },
    {
      Indicator: 'Total income cash',
      Value: parseInt(dd.cash_cc.cash, 10) || 0,
      Description: 'Cash income total',
    },
    {
      Indicator: 'Total Income CC',
      Value: parseInt(dd.cash_cc.cc, 10) || 0,
      Description: 'Card income total',
    },
    {
      Indicator: 'Total Devolution',
      Value: refundVal,
      Description: 'Total refunds',
    },
  ]
}

function pad2(n) {
  return String(n ?? '').padStart(2, '0')
}

function mergeDailyAverageRows(dd) {
  const map = new Map()
  const put = (list, fieldName) => {
    if (!Array.isArray(list)) return
    list.forEach(item => {
      const y = item.year ?? item.YYYY ?? ''
      const m = pad2(item.month ?? item.MM ?? '')
      const d = pad2(item.day ?? item.DD ?? '')
      const dateKey = `${y}-${m}-${d}`
      if (!map.has(dateKey)) map.set(dateKey, { Date: dateKey })
      const row = map.get(dateKey)
      const hasPrimary = item[fieldName] != null && item[fieldName] !== ''
      const hasLegacy =
        fieldName !== 'AVG_NP' &&
        item.AVG_NP != null &&
        item.AVG_NP !== ''
      if (hasPrimary || hasLegacy) {
        row[fieldName] = readDailyAverageValue(item, fieldName)
      }
    })
  }
  if (dd?.average) {
    // A9: exportar alias correctos (incl. series tipadas)
    put(dd.average.total_daily, 'AVG_TOTAL')
    put(dd.average.fu_daily, 'AVG_FU')
    put(dd.average.pi_daily, 'AVG_PI')
    put(dd.average.pp_daily, 'AVG_PP')
    put(dd.average.rs_daily, 'AVG_RS')
    put(dd.average.cv_daily, 'AVG_CV')
  }
  return Array.from(map.values()).sort((a, b) =>
    String(a.Date).localeCompare(String(b.Date))
  )
}

function buildPatientsPerDay(dd) {
  if (!Array.isArray(dd?.patients_per_day)) return []
  return dd.patients_per_day.map(e => ({
    Year: e.year,
    Month: e.month,
    Day: e.day,
    Count: Number(e.count ?? e.total ?? e.TOTAL ?? 0),
  }))
}

function buildPatientsPerMonth(dd) {
  if (!Array.isArray(dd?.patients_per_month)) return []
  return dd.patients_per_month.map(e => ({
    Year: e.year,
    Month: e.month,
    Count: Number(e.count ?? e.total ?? e.TOTAL ?? 0),
  }))
}

function buildVisitTypeTotals(dd) {
  const prefixToLabel = {
    'F/U': 'Follow Up',
    'N/C': 'New Consultation',
    'N/P': 'New patient',
    'P/I': 'Pending Injection',
    'P/P': 'Pending Procedure',
    'R/S': 'Result',
    'P/S': 'Pregnancy Screening',
    'V/C': 'COVID-19 Vaccination',
    'M/B': 'Membership',
    'IV/NP': 'IV therapies - NP',
    'IV/NC': 'IV therapies - NC',
  }
  const labels = Object.values(prefixToLabel).reduce((acc, name) => {
    acc[name] = 0
    return acc
  }, {})

  // Preferir patients_by_type (sin memberships.NP/NC ni INS-*)
  if (Array.isArray(dd?.patients_by_type) && dd.patients_by_type.length > 0) {
    dd.patients_by_type.forEach(row => {
      const prefix = row && row.prefix
      if (!prefix || String(prefix).startsWith('INS-')) return
      const label = prefixToLabel[prefix]
      if (!label) return
      labels[label] += parseInt(row.count, 10) || 0
    })
  } else if (Array.isArray(dd?.year_data)) {
    dd.year_data.forEach(block => {
      ;(block.facilities || []).forEach(res => {
        Object.keys(prefixToLabel).forEach(prefix => {
          labels[prefixToLabel[prefix]] += parseInt(res[prefix], 10) || 0
        })
      })
    })
  }

  return Object.entries(labels).map(([Visit_type, Count]) => ({
    Visit_type,
    Count,
  }))
}

function buildYearFacilityRows(dd) {
  if (!Array.isArray(dd?.year_data)) return []
  const rows = []
  dd.year_data.forEach(entry => {
    const month = entry.month
    const year = entry.year
    const income = entry.income
    ;(entry.facilities || []).forEach((res, idx) => {
      const flat = { Month: month, Year: year, Month_income: income, Line: idx + 1 }
      Object.keys(res || {}).forEach(k => {
        if (k !== '__ob__') flat[k] = res[k]
      })
      rows.push(flat)
    })
  })
  return rows
}

function buildAverageByType(dd) {
  if (!Array.isArray(dd?.average?.by_type)) return []
  return dd.average.by_type
    .filter(row => (parseInt(row.count, 10) || 0) > 0 || (parseFloat(row.total) || 0) > 0)
    .map(row => ({
      Prefix: row.prefix ?? '',
      Name: row.name ?? '',
      Count: parseInt(row.count, 10) || 0,
      Total: parseFloat(row.total) || 0,
      Average: parseFloat(row.average) || 0,
    }))
}

function buildBonusRows(dd) {
  const bonus = dd?.bonus
  if (!bonus || typeof bonus !== 'object') return []
  const rows = []
  Object.entries(bonus).forEach(([location, dates]) => {
    if (!dates || typeof dates !== 'object') return
    Object.entries(dates).forEach(([date, payload]) => {
      if (date === '__ob__') return
      const appts = payload?.appointments
      rows.push({
        Location: location,
        Date: date,
        Total: payload?.total ?? '',
        Total_to_pay: payload?.total_to_pay ?? payload?.total ?? '',
        Num_appointments: Array.isArray(appts) ? appts.length : 0,
      })
    })
  })
  return rows
}

function buildMembershipSummary(dd) {
  const m = dd?.memberships
  if (!m) return []
  return [
    { Type: 'NP', Global_count: m.NP || 0 },
    { Type: 'NC', Global_count: m.NC || 0 },
  ]
}

function buildMembershipByLocation(dd) {
  const data = dd?.memberships?.data
  if (!data || typeof data !== 'object') return []
  const rows = []
  ;['NP', 'NC'].forEach(tipo => {
    const obj = data[tipo]
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return
    Object.entries(obj).forEach(([facility, count]) => {
      rows.push({ Type: tipo, Facility: facility, Count: count })
    })
  })
  return rows
}

function sheetOrPlaceholder(name, rows, placeholder) {
  const data = rows && rows.length ? rows : [{ Note: placeholder }]
  return { name, data }
}

export function getDashboardSheets(dataDashboard, meta) {
  const metaRows = buildMetadata(meta)
  return [
    {
      name: 'Metadata',
      data: metaRows.length ? metaRows : [{ Note: 'No metadata' }],
    },
    sheetOrPlaceholder(
      'Indicators',
      buildFinancialIndicators(dataDashboard),
      'No indicators'
    ),
    sheetOrPlaceholder(
      'Daily_averages',
      mergeDailyAverageRows(dataDashboard),
      'No daily averages'
    ),
    sheetOrPlaceholder(
      'Patients_per_day',
      buildPatientsPerDay(dataDashboard),
      'No patients per day'
    ),
    sheetOrPlaceholder(
      'Patients_per_month',
      buildPatientsPerMonth(dataDashboard),
      'No patients per month'
    ),
    sheetOrPlaceholder(
      'Visit_types',
      buildVisitTypeTotals(dataDashboard),
      'No visit types'
    ),
    sheetOrPlaceholder(
      'Average_by_type',
      buildAverageByType(dataDashboard),
      'No averages by visit type'
    ),
    sheetOrPlaceholder(
      'Visits_month_facility',
      buildYearFacilityRows(dataDashboard),
      'No visits by month and facility'
    ),
    sheetOrPlaceholder('Bonus', buildBonusRows(dataDashboard), 'No bonus data'),
    sheetOrPlaceholder(
      'Membership_summary',
      buildMembershipSummary(dataDashboard),
      'No membership data'
    ),
    sheetOrPlaceholder(
      'Membership_by_facility',
      buildMembershipByLocation(dataDashboard),
      'No membership by facility'
    ),
  ]
}

export function exportDashboardToExcel(dataDashboard, meta) {
  const sheets = getDashboardSheets(dataDashboard, meta)
  const wb = XLSX.utils.book_new()
  sheets.forEach(({ name, data }) => {
    const ws = XLSX.utils.json_to_sheet(data)
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(name))
  })
  const start = (meta?.start || 'start').replace(/[^\d-]/g, '')
  const end = (meta?.end || 'end').replace(/[^\d-]/g, '')
  const filename = `dashboard_${start}_${end}.xlsx`
  XLSX.writeFile(wb, filename)
}

export function exportDashboardToCsv(dataDashboard, meta) {
  const sheets = getDashboardSheets(dataDashboard, meta)
  let text = ''
  sheets.forEach(({ name, data }) => {
    text += `\n\n=== ${name} ===\n`
    if (!data || !data.length) {
      text += '(no data)\n'
      return
    }
    const keys = Object.keys(data[0])
    text += `${keys.map(escapeCsvCell).join(',')}\n`
    data.forEach(row => {
      text += `${keys.map(k => escapeCsvCell(row[k])).join(',')}\n`
    })
  })
  const blob = new Blob(['\ufeff' + text.trimStart()], {
    type: 'text/csv;charset=utf-8;',
  })
  const start = (meta?.start || 'start').replace(/[^\d-]/g, '')
  const end = (meta?.end || 'end').replace(/[^\d-]/g, '')
  const filename = `dashboard_${start}_${end}.csv`
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
